/* eslint-disable sonarjs/cognitive-complexity */
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { FRONTEND_LOGS } from '../../../brands/default/config';
import { CREDIT } from '../../../brands/default/content/components/checkout';
import {
  getUsableWalletBalance,
  getWalletBalance,
  getWalletTotalBalance,
  hasError,
  isShowCredit,
  notEligibleForAnyWallet,
  setUpWalletBalances,
} from '../../../helpers/credit';
import { formatPrice } from '../../../helpers/currency';
import { logToSplunk } from '../../_generic/axiosSplunk/axiosSplunk';

const Credit = ({
  currency,
  paymentOptions,
  promoCodeIsApplied,
  isWalletAllowed,
  isGiftClaim = false,
  isGiftClaimable = false,
  totalWalletApplicableOrderAmount,
  currencySymbol,
  handleCreditClick,
  vipOptInTotalWalletApplicableOrderAmount,
  walletIsApplied = false,
}) => {
  const [isChecked, setIsChecked] = useState(walletIsApplied);

  useEffect(() => {
    setIsChecked(walletIsApplied);
    logToSplunk(
      {
        component: 'Credit',
        isWalletApplied: walletIsApplied,
        type: 'Wallet status - on load',
      },
      FRONTEND_LOGS,
    );
  }, [walletIsApplied]);

  useEffect(() => {
    if (isGiftClaim) {
      logToSplunk(
        {
          component: 'Credit',
          isGiftClaim,
          isWalletApplied: isGiftClaim,
          type: 'Is Gift Claim - on load',
        },
        FRONTEND_LOGS,
      );
      setIsChecked(isGiftClaim);
      handleCreditClick(isGiftClaim, true);
    }
  }, [handleCreditClick, isGiftClaim]);

  const walletBalances = setUpWalletBalances({ paymentOptions });
  const hasWalletBalances = Object.keys(walletBalances).length > 0;
  const hasCreditInSameCurrency =
    hasWalletBalances && walletBalances[currency].totalBalance > 0;
  const walletHasRefundableBalance =
    hasWalletBalances && walletBalances[currency].refundableBalance > 0;
  const amountInWallet = getWalletTotalBalance({
    currency,
    hasWalletBalances,
    walletBalances,
  });

  const usableAmountInWallet = getUsableWalletBalance({
    amountInWallet,
    currency,
    hasWalletBalances,
    isWalletAllowed,
    promoCodeIsApplied,
    walletBalances,
  });

  let totalUsedFromWallet = getWalletBalance({
    hasWalletBalances,
    totalWalletApplicableOrderAmount,
    usableAmountInWallet,
  });
  if (vipOptInTotalWalletApplicableOrderAmount)
    totalUsedFromWallet = vipOptInTotalWalletApplicableOrderAmount;

  const isDisabled = () => {
    if (!hasWalletBalances) return null;

    return promoCodeIsApplied && !walletBalances[currency].refundableBalance;
  };

  // Select message to use
  const showLargeMessage =
    amountInWallet > usableAmountInWallet ||
    amountInWallet > totalWalletApplicableOrderAmount;

  const showDisabledZeroUsedMessage = isDisabled() && totalUsedFromWallet === 0;

  const handleInputChange = () => {
    logToSplunk(
      {
        isWalletApplied: !isChecked,
        type: 'Wallet status',
      },
      FRONTEND_LOGS,
    );
    if (
      !isDisabled() &&
      !notEligibleForAnyWallet({
        currency,
        hasWalletBalances,
        isWalletAllowed,
        promoCodeIsApplied,
        totalWalletApplicableOrderAmount,
        usableAmountInWallet,
        walletBalances,
      })
    ) {
      logToSplunk(
        {
          component: 'Credit',
          isWalletApplied: !isChecked,
          type: 'Wallet checkbox input change',
        },
        FRONTEND_LOGS,
      );
      setIsChecked(!isChecked);
      handleCreditClick(!isChecked, false);
    }
  };

  return (
    <>
      {isShowCredit({
        hasCreditInSameCurrency,
        isWalletAllowed,
        totalWalletApplicableOrderAmount,
        walletHasRefundableBalance,
      }) && (
        <label className="credit" htmlFor="creditCheckbox">
          <input
            checked={isChecked}
            className="credit__checkbox"
            disabled={isDisabled()}
            id="creditCheckbox"
            onChange={handleInputChange}
            type="checkbox"
          />
          {!showLargeMessage && !showDisabledZeroUsedMessage && (
            <span>
              {CREDIT.msgPurchase(
                currencySymbol + formatPrice(totalUsedFromWallet, true),
              )}
            </span>
          )}
          {showLargeMessage &&
            !showDisabledZeroUsedMessage &&
            !isGiftClaimable && (
              <span>
                {CREDIT.msgLarge(
                  currencySymbol + formatPrice(totalUsedFromWallet, true),
                  currencySymbol + formatPrice(amountInWallet, true),
                )}
              </span>
            )}
          {showLargeMessage &&
            !showDisabledZeroUsedMessage &&
            isGiftClaimable && (
              <span>
                {CREDIT.expMsgPurchase(
                  currencySymbol + formatPrice(totalUsedFromWallet, true),
                )}
              </span>
            )}
          {showDisabledZeroUsedMessage && (
            <span>
              {CREDIT.msgZero(
                currencySymbol + formatPrice(amountInWallet, true),
              )}
            </span>
          )}
          {isDisabled() ||
            (Boolean(
              hasError({
                currency,
                hasWalletBalances,
                isWalletAllowed,
                promoCodeIsApplied,
                totalWalletApplicableOrderAmount,
                usableAmountInWallet,
                walletBalances,
              }),
            ) && (
              <p className="credit__error">
                <span>
                  {CREDIT.msgCannot(
                    currencySymbol +
                      formatPrice(
                        walletBalances[currency].marketingBalance,
                        true,
                      ),
                  )}
                </span>
                {isWalletAllowed && <span>{CREDIT.msgAllowed}</span>}
                {!isWalletAllowed && <span>{CREDIT.msgNotAllowed}</span>}
              </p>
            ))}
        </label>
      )}
      <style jsx>{`
        .credit {
          cursor: pointer;
          margin-bottom: 10px;
          border: 1px solid #c7c7c7;
          padding: 10px;
        }
        .credit__checkbox {
          margin-right: 5px;
        }
        .credit__error {
          color: #eb3f3c;
        }
      `}</style>
    </>
  );
};

Credit.propTypes = {
  currency: PropTypes.string.isRequired,
  currencySymbol: PropTypes.string,
  handleCreditClick: PropTypes.func.isRequired,
  isGiftClaim: PropTypes.bool,
  isGiftClaimable: PropTypes.bool,
  isWalletAllowed: PropTypes.bool,
  paymentOptions: PropTypes.object,
  promoCodeIsApplied: PropTypes.bool,
  totalWalletApplicableOrderAmount: PropTypes.number,
  vipOptInTotalWalletApplicableOrderAmount: PropTypes.number,
  walletIsApplied: PropTypes.bool,
};

export default Credit;
